'use client';

import { type FC } from 'react';
import { Avatar } from '../../../ui';
import { FaTrophy } from '../../../icons';
import { formatCreditsNumber } from '../../../../utils/formatters';
import { WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, weaponKeyOf } from '../../spriteLayout';
import { AtlasIcon } from './AtlasIcon';
import type { WeaponClass } from '@cs-shot/battle-sim';
import type { Ally, Side } from '../useRushMidMatch';
import { rushMidAvatarUrl } from '../avatarUrl';
import { useLocalTranslation } from '../../../../hooks/useLocalTranslation';
import translations from '../RushMid.translations';

const weaponImg = (side: Side, w: WeaponClass) => WEAPON_ICON_SRC[weaponKeyOf(side, w)];

const RatingLabel: FC<{ rating?: number }> = ({ rating }) => (
  <div className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold tabular-nums text-label-secondary">
    <FaTrophy size={10} className="text-side-t" />
    {rating === undefined ? '—' : formatCreditsNumber(rating)}
  </div>
);

interface RosterRowProps {
  side: Side;
  member: Ally;
  fallbackKeyPrefix: string;
  showLoadout?: boolean;
}

export const RosterRow: FC<RosterRowProps> = ({ side, member, fallbackKeyPrefix, showLoadout }) => {
  const { t } = useLocalTranslation(translations);
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-bg-secondary p-2.5">
      <Avatar
        size="m"
        src={rushMidAvatarUrl(member.userId)}
        firstName={member.name}
        fallbackKey={member.userId ?? `${fallbackKeyPrefix}-${member.index}`}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-label-primary">{member.name}</div>
        <RatingLabel rating={member.rating} />
      </div>
      {showLoadout && (
        <div className="ml-auto flex items-center gap-2">
          {member.loadout ? (
            <>
              <AtlasIcon src={weaponImg(side, member.loadout.weapon)} size={44} />
              {member.loadout.grenade && <AtlasIcon src={GRENADE_ICON_SRC} size={20} />}
              {member.loadout.smoke && <AtlasIcon src={SMOKE_ICON_SRC} size={20} />}
              {member.loadout.flash && <AtlasIcon src={FLASH_ICON_SRC} size={20} />}
            </>
          ) : (
            <span className="text-xs italic text-label-secondary">{t('choosing')}</span>
          )}
        </div>
      )}
    </div>
  );
};
